import * as DialogPrimitive from '@radix-ui/react-dialog'
import { AlertTriangle, X, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export type ModalTone = 'danger' | 'warning' | 'info'

export interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tone?: ModalTone
  /** Icon override — defaults to AlertTriangle for danger/warning. */
  icon?: LucideIcon
  title: React.ReactNode
  description?: React.ReactNode
  /** Footer actions rendered right-aligned. */
  footer: React.ReactNode
  /** Body content between description and footer. */
  children?: React.ReactNode
  /** Type scale. 'form' = the larger data-entry scale (tokens.css), as on Drawer. */
  surface?: 'form'
}

const toneIconClass: Record<ModalTone, string> = {
  danger: 'bg-danger-bg text-danger-text',
  warning: 'bg-warning-bg text-warning-text',
  info: 'bg-info-bg text-info-text',
}

/**
 * Principle 4 modal: destructive confirmations and irreversible actions
 * only. Dims the background, traps focus, dismissible with Esc + close X.
 * Never use for data entry — that belongs in a drawer or dedicated page.
 */
export function Modal({
  open,
  onOpenChange,
  tone = 'danger',
  icon: Icon = AlertTriangle,
  title,
  description,
  footer,
  children,
  surface,
}: ModalProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-overlay bg-bg-overlay',
            'animate-in fade-in-0 duration-fast',
          )}
        />
        <DialogPrimitive.Content
          data-surface={surface}
          className={cn(
            'fixed left-1/2 top-1/2 z-modal w-[440px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            'rounded-lg border border-border-default bg-surface shadow-xl',
            'animate-in fade-in-0 zoom-in-95 duration-fast',
          )}
        >
          <header className="flex items-start gap-3 p-5">
            <span
              aria-hidden="true"
              className={cn(
                'inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
                toneIconClass[tone],
              )}
            >
              <Icon size={18} />
            </span>
            <div className="flex-1 pt-0.5">
              <DialogPrimitive.Title className="text-lg font-semibold leading-tight text-primary">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-sm leading-normal text-secondary">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="inline-flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
              >
                <X size={14} />
              </button>
            </DialogPrimitive.Close>
          </header>
          {children ? <div className="px-5 pb-2 text-sm text-secondary">{children}</div> : null}
          <footer className="flex items-center justify-end gap-2 border-t border-border-subtle bg-surface-sunk px-5 py-3">
            {footer}
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/** Low-level close + trigger primitives for cases where Modal's layout isn't enough.
 *  These are const re-exports of Radix primitives so the fast-refresh rule
 *  needs an opt-out here — they're components, just not detected as such by
 *  the lint plugin. */
/* eslint-disable react-refresh/only-export-components */
export const ModalClose = DialogPrimitive.Close
export const ModalTrigger = DialogPrimitive.Trigger
/* eslint-enable react-refresh/only-export-components */
